<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { getLanguageName } from 'dashboard/components/widgets/conversation/advancedFilterItems/languages';
import ContactDetailsItem from './ContactDetailsItem.vue';
import CustomAttributes from './customAttributes/CustomAttributes.vue';

const { t } = useI18n();

const props = defineProps({
  conversationAttributes: {
    type: Object,
    default: () => ({}),
  },
  contactAttributes: {
    type: Object,
    default: () => ({}),
  },
});

const refererRaw = computed(() => props.conversationAttributes.referer || '');

// 解析 referer，支持 <> 分隔符格式（来源页面<>Widget页面）
const refererUrls = computed(() => {
  let raw = refererRaw.value;
  if (!raw) return [];

  if (raw.includes('<>')) {
    return raw
      .split('<>')
      .map(url => url.trim())
      .filter(url => url.length > 0);
  }
  return [raw];
});

// 兼容旧逻辑：取第一个 URL 作为主 referer
const referer = computed(() => refererUrls.value[0] || '');
const initiatedAt = computed(
  () => props.conversationAttributes.initiated_at?.timestamp
);

const browserInfo = computed(() => props.conversationAttributes.browser);

const browserName = computed(() => {
  if (!browserInfo.value) return '';
  const { browser_name: name = '', browser_version: version = '' } =
    browserInfo.value;
  return `${name} ${version}`;
});

const browserLanguage = computed(() =>
  getLanguageName(props.conversationAttributes.browser_language)
);

const widgetLanguage = computed(() => {
  const lang = props.conversationAttributes.widget_language;
  if (!lang) return '';

  // 处理特殊值：获取客户端语言
  if (lang === 'browser') {
    return t(
      'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.LANGUAGE_SETTING.BROWSER'
    );
  }
  if (lang === 'site') {
    return t('INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.LANGUAGE_SETTING.SITE');
  }

  // 处理包含下划线的语言代码（如 zh_TW, pt_BR）
  // 只取前面部分去获取语言名称
  let languageCode = lang;
  if (lang.includes('_')) {
    languageCode = lang.split('_')[0];
  }

  // 获取语言名称，如果获取不到则返回原始语言代码
  const languageName = getLanguageName(languageCode);
  return languageName || lang;
});

/**
 * 【对接方式功能 - 步骤7】客服端显示对接方式
 *
 * 数据来源：conversation.additional_attributes.integration_type
 * 数据流向：后端 → WebSocket/API → currentChat → conversationAttributes → 此组件
 *
 * 对接方式说明：
 * - 'sdk': 脚本嵌入方式 - 客户通过 <script> 标签将 SDK 嵌入到自己网站
 * - 'download': 下载方式 - 客户下载 chat.html 文件部署到自己服务器
 * - 'link': 链接方式 - 用户直接访问 /widget?website_token=xxx 链接
 */
const integrationType = computed(() => {
  const type = props.conversationAttributes.integration_type;
  if (!type) return '';

  // 对接方式翻译键映射
  const typeMap = {
    sdk: 'CONTACT_PANEL.INTEGRATION_TYPE_OPTIONS.SDK',
    download: 'CONTACT_PANEL.INTEGRATION_TYPE_OPTIONS.DOWNLOAD',
    link: 'CONTACT_PANEL.INTEGRATION_TYPE_OPTIONS.LINK',
  };

  return t(typeMap[type]);
});

const platformName = computed(() => {
  if (!browserInfo.value) return '';
  const { platform_name: name = '', platform_version: version = '' } =
    browserInfo.value;
  return `${name} ${version}`;
});

const createdAtIp = computed(() => props.contactAttributes.created_at_ip);

const staticElements = computed(() =>
  [
    {
      content: initiatedAt,
      title: 'CONTACT_PANEL.INITIATED_AT',
    },
    {
      content: browserLanguage,
      title: 'CONTACT_PANEL.BROWSER_LANGUAGE',
    },
    {
      content: widgetLanguage,
      title: 'CONTACT_PANEL.WIDGET_LANGUAGE',
    },
    {
      content: integrationType,
      title: 'CONTACT_PANEL.INTEGRATION_TYPE',
    },
    {
      content: referer,
      title: 'CONTACT_PANEL.INITIATED_FROM',
      type: 'link',
    },
    {
      content: browserName,
      title: 'CONTACT_PANEL.BROWSER',
    },
    {
      content: platformName,
      title: 'CONTACT_PANEL.OS',
    },
    {
      content: createdAtIp,
      title: 'CONTACT_PANEL.IP_ADDRESS',
    },
  ].filter(attribute => !!attribute.content.value)
);

const evenClass = [
  '[&>*:nth-child(odd)]:!bg-white [&>*:nth-child(even)]:!bg-slate-25',
  'dark:[&>*:nth-child(odd)]:!bg-slate-900 dark:[&>*:nth-child(even)]:!bg-slate-800/50',
];
</script>

<template>
  <div class="conversation--details">
    <div :class="evenClass">
      <ContactDetailsItem
        v-for="element in staticElements"
        :key="element.title"
        :title="$t(element.title)"
        :value="element.content.value"
        class="border-b border-solid border-slate-50 dark:border-slate-700/50"
      >
        <template v-if="element.type === 'link'">
          <div
            v-for="(url, index) in refererUrls"
            :key="index"
            :class="{ 'mt-2': index > 0 }"
          >
            <a
              :href="url"
              rel="noopener noreferrer nofollow"
              target="_blank"
              class="break-all text-woot-400 dark:text-woot-600"
            >
              {{ url }}
            </a>
          </div>
        </template>
      </ContactDetailsItem>
    </div>
    <CustomAttributes
      :start-at="staticElements.length % 2 === 0 ? 'even' : 'odd'"
      attribute-class="conversation--attribute"
      attribute-from="conversation_panel"
      attribute-type="conversation_attribute"
    />
  </div>
</template>
